import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlateCombobox } from "@/components/PlateCombobox";
import { api, errMsg } from "@/lib/api";

export default function LogbookPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const empty = { key: "", assignment_id: "", as_co_driver: false, plate: "", km_start: "", km_end: "", swap: false, swap_km: "", swap_time: "", fuel: "" };
  const [f, setF] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const load = useCallback(() => { api.get(`/logbook/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e))); }, [token]);
  useEffect(() => { load(); }, [load]);

  const keyOf = (d) => `${d.assignment_id}${d.co ? ":co" : ""}`;
  const me = info?.drivers.find((d) => keyOf(d) === f.key);
  const pick = (k) => { const d = info.drivers.find((x) => keyOf(x) === k); setF((p) => ({ ...p, key: k, assignment_id: d?.assignment_id || "", as_co_driver: !!d?.co, plate: d?.plate || "", swap: !!d?.partner && p.swap })); };
  const ks = Number(f.km_start), ke = Number(f.km_end), sk = Number(f.swap_km);
  const swapOk = !f.swap || (f.swap_km !== "" && f.swap_time && sk >= ks && sk <= ke);
  const valid = f.assignment_id && f.plate && f.km_start !== "" && f.km_end !== "" && ke >= ks && swapOk;
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/logbook/${token}`, { assignment_id: f.assignment_id, as_co_driver: f.as_co_driver, plate: f.plate, km_start: ks, km_end: ke,
        swap_km: f.swap ? sk : null, swap_time: f.swap ? f.swap_time : null, fuel_amount: info.driver_fuel && f.fuel !== "" ? Number(f.fuel) : null });
      setDone({ name: me?.name, km: data.km_total });
      setF(empty);
      load();
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-md">
        <div className="mb-6 flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-blue-600"><Truck className="h-5 w-5 text-white" /></div>
          <div>
            <h1 className="text-2xl font-bold">Giornale di bordo</h1>
            <p className="text-sm text-slate-500" data-testid="logbook-header">{info ? `${info.station_name} · ${new Date(info.date).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}` : error ? "" : "Caricamento..."}</p>
          </div>
        </div>
        {done && (
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800" data-testid="logbook-success">
            <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
            <p>Grazie {done.name}, giornale registrato: <b>{done.km.toLocaleString("it-IT")} km</b>.</p>
          </div>
        )}
        {error && <p className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-700" data-testid="logbook-error">{error}</p>}
        {info && (info.drivers.length === 0 ? <p className="rounded-xl border bg-white p-6 text-center text-slate-500" data-testid="logbook-empty">Nessuna assegnazione caricata per oggi.</p> : (
          <div className="space-y-4 rounded-xl border bg-white p-5">
            <div className="space-y-1.5"><Label>Il tuo nome</Label>
              <PlateCombobox value={f.key} onChange={pick} testId="logbook-driver-select" placeholder="Seleziona il tuo nome" searchPlaceholder="Cerca nome..."
                options={info.drivers.map((d) => ({ value: keyOf(d), label: `${d.name}${d.partner ? ` (con ${d.partner})` : ""}${d.locked ? " · inserito dall'operatore" : d.done ? " ✓ già compilato" : ""}` }))} />
              {me?.partner && <p className="text-xs text-indigo-700" data-testid="logbook-partner">Doppio equipaggio con {me.partner}: basta compilarlo una volta per il furgone.</p>}
            </div>
            <div className="space-y-1.5"><Label>Furgone guidato</Label>
              <PlateCombobox value={f.plate} onChange={(v) => setF((p) => ({ ...p, plate: v }))} testId="logbook-plate-select" options={info.plates.map((p) => ({ value: p, label: p }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Km partenza</Label><Input type="number" inputMode="numeric" min="0" value={f.km_start} onChange={(e) => setF((p) => ({ ...p, km_start: e.target.value }))} data-testid="logbook-km-start" /></div>
              <div className="space-y-1.5"><Label>Km arrivo</Label><Input type="number" inputMode="numeric" min="0" value={f.km_end} onChange={(e) => setF((p) => ({ ...p, km_end: e.target.value }))} data-testid="logbook-km-end" /></div>
            </div>
            <p className="text-sm text-slate-600">Totale: <b className="font-mono-num" data-testid="logbook-km-total">{f.km_start !== "" && f.km_end !== "" ? (ke - ks).toLocaleString("it-IT") : "—"} km</b>
              {f.km_end !== "" && ke < ks && <span className="ml-2 text-rose-600">I km di arrivo devono essere maggiori</span>}</p>
            {me?.partner && (
              <div className="space-y-3 rounded-lg border border-indigo-200 bg-indigo-50/50 p-3" data-testid="logbook-swap-box">
                <label className="flex items-center gap-2 text-sm font-semibold"><input type="checkbox" className="h-4 w-4" checked={f.swap} onChange={(e) => setF((p) => ({ ...p, swap: e.target.checked }))} data-testid="logbook-swap-toggle" /> C'è stato il cambio pilota</label>
                <p className="text-xs text-indigo-800" data-testid="logbook-swap-order">1° alla guida: <b>{me.co ? me.partner : me.name}</b> → al cambio passa a <b>{me.co ? me.name : me.partner}</b></p>
                {f.swap && <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5"><Label>Km al cambio</Label><Input type="number" inputMode="numeric" min="0" value={f.swap_km} onChange={(e) => setF((p) => ({ ...p, swap_km: e.target.value }))} data-testid="logbook-swap-km" /></div>
                  <div className="space-y-1.5"><Label>Ora del cambio</Label><Input type="time" value={f.swap_time} onChange={(e) => setF((p) => ({ ...p, swap_time: e.target.value }))} data-testid="logbook-swap-time" /></div>
                  {f.swap_km !== "" && f.km_end !== "" && (sk < ks || sk > ke) && <p className="col-span-2 text-xs text-rose-600">I km al cambio devono essere tra partenza e arrivo</p>}
                </div>}
              </div>
            )}
            {info.driver_fuel && (
              <div className="space-y-1.5"><Label>Gasolio messo oggi (€)</Label>
                <Input type="number" inputMode="decimal" step="0.01" min="0" value={f.fuel} onChange={(e) => setF((p) => ({ ...p, fuel: e.target.value }))} placeholder="Lascia vuoto se non hai fatto rifornimento" data-testid="logbook-fuel" />
                <p className="text-xs text-slate-500">L'importo verrà controllato dall'operatore.</p>
              </div>
            )}
            <Button className="h-12 w-full text-base" onClick={submit} disabled={!valid || busy} data-testid="logbook-submit">{busy ? "Invio..." : "Invia giornale di bordo"}</Button>
            <p className="text-xs text-slate-400">Puoi correggere i dati inviandoli di nuovo entro mezzanotte.</p>
          </div>
        ))}
      </div>
    </div>
  );
}
